import { Injectable } from '@angular/core';
import { HTTP, HTTPResponse } from '@ionic-native/http/ngx';
import { HttpClient } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { HttpCommon } from '../util/utils';
import { AlertService } from '../util/alert.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class HttpClientService {
    private isNative = false;
    private sessionId: string;
    private clientType: string;
    private invalidSession: Subject<void> = new Subject<void>();
    private errorCode: Subject<number> = new Subject<number>();

    constructor(
        private nativeHttp: HTTP,
        private http: HttpClient,
        private platform: Platform,
        private alert: AlertService,
        private translate: TranslateService
    ) {
        this.isNative = this.platform.is('capacitor') || this.platform.is('cordova');
        if (this.isNative) {
            this.nativeHttp.setDataSerializer('json');
            this.nativeHttp.setServerTrustMode('default').catch((error) => {
                console.error(error);
            });
        }
        this.clientType = this.getClientType();
    }

    getInvalidSessionObservable() {
        return this.invalidSession.asObservable();
    }

    setSessionId(newSessionId: string) {
        this.sessionId = newSessionId;
    }

    getClientType(): string {
        if (this.platform.is('capacitor') && this.platform.is('ios')) {
            return 'ios';
        } else if (this.platform.is('capacitor') && this.platform.is('android')) {
            return 'android';
        } else {
            return 'web';
        }
    }

    /**
     * Make a POST request.
     * If app is run on native device (cordova,capacitor) then use native HTTP method (avoids CORS)
     * Otherwise use angular HTTP requests for testing in browser.
     * session_id, required headers and error handlers are implemented
     * @param action API Action
     * @param payload Payload of the POST request
     * @param dataHandler Optional function that gets data if it needs some pre-processing before returning it to actual promise.
     * Default implementation of this function just passes on the data to Promise.
     * @param customAlert pass this param if you want to display a custom alert. Default
     * alert wont be shown if server response is 'failure' or general server error.
     * Use this and '.catch()' if you want to show custom alert instead of default.
     * Error code to replace with custom alert
     * @returns returns a promise that will resolve on success and return only response data,
     * and reject on failure with appropriate message from `Messsages` util
     */
    public post(
        action: string,
        payload: any,
        dataHandler: (data: any, code?: any) => {} = (data) => {
            return data;
        },
        customAlert?: {
            showDefaultAlert: boolean;
            errorCode?: number;
        }
    ): Promise<any> {
        return this.isNative
            ? this.nativePost(action, payload, dataHandler, customAlert)
            : this.webPost(action, payload, dataHandler, customAlert);
    }

    private nativePost(
        action: string,
        payload: any,
        dataHandler: (data: any, code?: any) => {},
        customAlert?: {
            showDefaultAlert: boolean;
            errorCode?: number;
        }
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestBody = {
                action,
                payload,
                session_id: this.sessionId,
                app_version: environment.version,
                client_type: this.clientType
            };
            this.nativeHttp
                .post(environment.baseUrl, requestBody, {})
                .then((response: HTTPResponse) => {
                    const responseData = JSON.parse(response.data);
                    if (HttpCommon.isSuccessful(responseData)) {
                        resolve(dataHandler(responseData.response, responseData.code));
                    } else {
                        this.handleFailure(responseData, reject, customAlert);
                    }
                    if (responseData.code) {
                        this.errorCode.next(responseData.code);
                    }
                })
                .catch((error) => {
                    this.handleError(error, reject, customAlert);
                });
        });
    }

    private webPost(
        action: string,
        payload: any,
        dataHandler: (data: any, code?: any) => {},
        customAlert?: {
            showDefaultAlert: boolean;
            errorCode?: number;
        }
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestBody = {
                action,
                payload,
                client_type: this.clientType,
                app_version: environment.version
            };
            this.http
                .post(environment.baseUrl, requestBody)
                .toPromise()
                .then((response: any) => {
                    if (HttpCommon.isSuccessful(response)) {
                        resolve(dataHandler(response.response, { code: response.code, message: response.message }));
                    } else {
                        this.handleFailure(response, reject, customAlert);
                    }
                    if (response.code) {
                        this.errorCode.next(response.code);
                    }
                })
                .catch((error) => {
                    this.handleError(error, reject, customAlert);
                });
        });
    }

    shouldShowAlert(customAlert: { showDefaultAlert: boolean; errorCode?: number }, responseCode: number): boolean {
        if (customAlert) {
            if (!customAlert.showDefaultAlert) {
                return customAlert.errorCode !== undefined && customAlert.errorCode !== responseCode;
            }
        }
        return true;
    }

    private async handleError(
        error: any,
        reject: (reason?: any) => void,
        customAlert?: {
            showDefaultAlert: boolean;
            errorCode?: number;
        }
    ) {
        console.error(error);
        if (this.shouldShowAlert(customAlert, error.code)) {
            this.alert.show('utilsMessages.error.serverError');
        }

        reject(await this.translate.get('utilsMessages.error.serverError').toPromise());
    }

    private async handleFailure(
        response: any,
        reject: (reason?: any) => void,
        customAlert?: {
            showDefaultAlert: boolean;
            errorCode?: number;
        }
    ) {
        console.error(response);
        let res: string = await this.translate.get('utilsMessages.error.tryAgain').toPromise();

        if (response.hasOwnProperty('message')) {
            res = response.message;
            if (response.message === 'invalid session') {
                this.invalidSession.next();
                reject(res);
                return;
            }
        }

        if (this.shouldShowAlert(customAlert, response.code)) {
            this.alert.show(res);
        }

        reject({ message: res, code: customAlert ? response.code : undefined });
    }

    get responseCodeObservable(): Observable<number> {
        return this.errorCode.asObservable();
    }
}
